import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormInput, InputProps, Modal, ModalActions, ModalProps } from '@pletly/ui';
import { PasswordRequirementsHint } from '../PasswordRequirementsHint';
import { Password } from '../Password';
import { useChangePasswordModal } from './useChangePasswordModal';
import { withTranslation } from '../../hoc/withTranslation';

const PasswordInput = ({ label, ...props }: InputProps & { name: string }) => (
	<FormInput
		fullWidth
		gutter
		accessoryLeft={undefined}
		label={label}
		placeholder={typeof label === 'string' ? label : undefined}
		InputComponent={Password}
		{...props}
	/>
);

export const ChangePasswordModal = withTranslation(
	({ visible, onClose, skipOldPassword, onChangePassword }: Props) => {
		const { t } = useTranslation();

		const { formMethods, loading, onSubmit } = useChangePasswordModal({
			visible,
			onClose,
			onChangePassword,
		});

		const newPassword = formMethods.watch('newPassword');

		return (
			<Modal
				visible={visible}
				title={t('change-password.modal.title')}
				withOwnToast={false}
				actions={
					<ModalActions
						primaryAction={{
							label: t('change-password.modal.submit'),
							onPress: onSubmit,
							variant: 'filled',
							loading,
							disabled: loading,
						}}
						secondaryAction={{
							label: t('common.cancel'),
							onPress: onClose,
							variant: 'outline',
							color: 'primary',
							disabled: loading,
						}}
					/>
				}
			>
				<FormProvider {...formMethods}>
					<PasswordRequirementsHint size="small" value={newPassword} />

					{!skipOldPassword && (
						<PasswordInput
							defaultValue=""
							name="password"
							textContentType="password"
							label={t('change-password.old-password')}
							disabled={loading}
						/>
					)}

					<PasswordInput
						name="newPassword"
						textContentType="newPassword"
						label={t('change-password.new-password')}
						disabled={loading}
					/>

					<PasswordInput
						name="repeatPassword"
						textContentType="newPassword"
						label={t('change-password.repeat-new-password')}
						disabled={loading}
					/>
				</FormProvider>
			</Modal>
		);
	},
);

type Props = {
	skipOldPassword?: boolean;
	onChangePassword: (data: { oldPassword?: string; newPassword: string }) => Promise<unknown>;
} & Pick<ModalProps, 'visible' | 'onClose'>;
